import { TailwindWrapper } from "@utils/TailwindWrapper";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@utils/cn";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";
import { Text, Button, Icon } from "@ui-component-library";
import SelectPropertyDialog from "./SelectPropertyDialog";
import SearchPropertyDialog from "./SearchPropertyDialog";

export const SelectProperty = ({ showSummary = false, emptyContent }) => {
    const {
        userProperties,
        selectedProperties,
        addSelectedProperty,
        removeSelectedProperty,
        openSelectPropertyDialog,
        setOpenSelectPropertyDialog,
        startedAsGuest,
    } = useSubscription();

    const [isPropertiesDialogOpen, setIsPropertiesDialogOpen] = useState(false);
    const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);

    useEffect(() => {
        // !showSummary to prevent opening two dialogs if SelectProperty is used in two places
        if (openSelectPropertyDialog && !showSummary) {
            startedAsGuest ? setIsSearchDialogOpen(true) : setIsPropertiesDialogOpen(true);
            setOpenSelectPropertyDialog(false);
        }
    }, [openSelectPropertyDialog]);

    const handleOpenDialog = () => {
        startedAsGuest ? setIsSearchDialogOpen(true) : setIsPropertiesDialogOpen(true);
    };

    const handlePropertySelect = (option) => {
        const property = userProperties.find((p) => p.ref === option.value);
        if (!selectedProperties.some((p) => p.ref === property.ref)) {
            addSelectedProperty(property);
        } else {
            removeSelectedProperty(property);
        }
    };

    const handleSearchConfirm = (data) => {
        data.properties.forEach((p) => {
            addSelectedProperty({
                designation: p.designation,
                ref: p.lmID,
                forestArea: p.forest_land,
                plus_price_error: p.plus_price_error ?? 0,
                plus_price: p.plus_price ?? 0,
                plus_price_max: p.plus_price_max ?? 0,
                type: p.type,
                lmID: p.lmID,
                dfr: p.dfr,
                hasBadData: p.has_bad_data,
                disabled: false,
            });
        });
    };

    const totalForestArea = selectedProperties.reduce(
        (acc, curr) => acc + curr.forestArea,
        0
    );
    const hasSelectedProperties = selectedProperties.length > 0;

    const hasBadData = selectedProperties.some(
        (property) => property?.hasBadData
    );
    const propertiesWithBadData = selectedProperties.filter(prop => prop.hasBadData);

    const title = startedAsGuest
        ? hasSelectedProperties ? `Valda fastigheter (${selectedProperties.length})` : "Sök efter fastigheter"
        : hasSelectedProperties ? `Valda fastigheter (${selectedProperties.length})` : "Välj fastigheter att aktivera plus på";

    const buttonText = startedAsGuest
        ? hasSelectedProperties ? "Sök fler" : "Sök"
        : hasSelectedProperties ? "Ändra" : "Lägg till";

    return (
        <>
            <div className={"flex flex-row gap-4 justify-start items-start"}>
                <Icon
                    tw
                    name="MapPinSimple"
                    size="large"
                    spacing="medium"
                    className="fill-brown-700 bg-stone-200"
                />
                <div
                    className={cn('flex flex-row gap-1 justify-between w-full', hasSelectedProperties ? "items-start" : "items-center")}

                >
                    <div className="flex flex-col gap-1">
                        <Text variant="text-sm/leading-normal/medium">
                            {title}
                        </Text>
                        {hasSelectedProperties && (
                            <Text
                                variant="text-sm/leading-normal/normal"
                                className="text-pebble-500"
                            >
                                {totalForestArea} hektar skogsmark
                            </Text>
                        )}
                        {showSummary && (
                            <Text variant="text-sm/leading-normal/normal" className="text-pebble-500 capitalize flex flex-col gap-1 mt-2">
                                {selectedProperties.map((p) => <span key={p.ref} className="capitalize">{p.designation.toLowerCase()}</span>)}
                            </Text>
                        )}
                    </div>
                    <Button
                        tw
                        variant="secondary"
                        size="small"
                        onClick={handleOpenDialog}
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>

            {!showSummary && selectedProperties.map((p) => (
                <>
                    <div className="w-full h-[0.5px] border border-stone-500/17" />
                    <div className="flex flex-col gap-6">
                        <div
                            key={p.ref}
                            className="flex flex-row gap-1 justify-between items-center"
                        >
                            <div className="flex flex-col gap-1">
                                <Text variant="text-sm/leading-normal/medium" className="capitalize">
                                    {p.designation.toLowerCase()}
                                </Text>
                                <Text
                                    variant="text-sm/leading-normal/normal"
                                    className="text-pebble-500"
                                >
                                    {!p.forestArea ? "..." : `${p.forestArea} hektar skogsmark`}
                                </Text>
                            </div>
                            <Button
                                tw
                                variant="outline"
                                size="small"
                                onClick={() => removeSelectedProperty(p)}
                            >
                                Ta bort
                            </Button>
                        </div>
                    </div>
                </>
            ))}

            {hasBadData && (
                <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3 mt-4">
                    <Icon
                        name="InfoFilled"
                        size="medium"
                        className="fill-pebble-500"
                    />
                    <div className="flex flex-col gap-2">
                        <Text
                            variant="text-xs/leading-normal/normal"
                            className="text-pebble-500"
                        >
                            Några av de fastigheter du har valt ligger i områden där den officiella skogsdatan är av <b>generellt lägre kvalitet</b>.
                        </Text>
                        <Text
                            variant="text-xs/leading-normal/medium"
                            className="text-pebble-500"
                        >
                            Fastigheter med begränsad datakvalitet:
                        </Text>
                        <ul className="list-disc pl-4">
                            {propertiesWithBadData.map((prop) => (
                                <Text as="li" key={prop.ref} variant="text-xs/leading-normal/normal" className="text-pebble-500 capitalize">
                                    {prop.designation}
                                </Text>
                            ))}
                        </ul>
                        <Text
                            variant="text-xs/leading-normal/normal"
                            className="text-pebble-500 mt-2"
                        >
                            Tänk på att viss information kan vara mindre exakt för dessa fastigheter. <span className="text-[#6856C3]">Treebula Plus</span> erbjuder ändå många insikter och funktioner, t.ex. virkespriser och marknadsinformation.
                        </Text>
                    </div>
                </div>
            )}

            {createPortal(
                <TailwindWrapper>
                    <SelectPropertyDialog
                        isOpen={isPropertiesDialogOpen}
                        onClose={() => setIsPropertiesDialogOpen(false)}
                        userProperties={userProperties}
                        selectedProperties={selectedProperties}
                        onSelect={handlePropertySelect}
                        emptyContent={emptyContent}
                    />
                </TailwindWrapper>,
                document.body
            )}

            {createPortal(
                <TailwindWrapper>
                    <SearchPropertyDialog
                        isOpen={isSearchDialogOpen}
                        onClose={() => setIsSearchDialogOpen(false)}
                        onConfirm={handleSearchConfirm}
                    />
                </TailwindWrapper>,
                document.body
            )}
        </>
    );
};